import axios from 'axios';
import { WORD_REGEX_EXPRESSION } from '../Utils/Text';

export interface SpeechMarker {
  characters?: string[];
  characterStartTimesSeconds?: number[];
  characterEndTimesSeconds?: number[];
}

export interface TextToSpeechDescriptor {
  audioUrl: string;
  markersUrl?: string;
}

interface TextBoundary {
  text: string;
  startIndex: number;
  endIndex: number;
}

export class TextToSpeechApi {
  async getTTSDescriptor(
    mediaDescriptorUrl: string,
  ): Promise<TextToSpeechDescriptor> {
    const response =
      await axios.get<TextToSpeechDescriptor>(mediaDescriptorUrl);
    return response.data;
  }

  async getSpeechMarkers(markersUrl: string): Promise<SpeechMarker[]> {
    const response = await axios.get<SpeechMarker[]>(markersUrl);
    return response.data;
  }

  findWordBoundaryAtTime(
    currentTime: number,
    markers: SpeechMarker[],
  ): TextBoundary | undefined {
    // The regex pattern matches how Article View breaks down the passage into words
    const regex = new RegExp(WORD_REGEX_EXPRESSION, 'gm');
    return this.findTextBoundaryAtTime(currentTime, regex, markers);
  }

  findTextBoundaryAtTime(
    currentTime: number,
    boundaryRegex: RegExp,
    markers?: SpeechMarker[],
  ): TextBoundary | undefined {
    // Assume the characters are sorted by their start time.
    // Find the first character that matches the regex, that is before or equal to the start time
    // Iterate and append to response until you find the next such character or end of text
    if (!markers?.length) return undefined;

    // Find the marker and character index for the current time
    let currentMarkerIndex = -1;
    let currentCharIndex = -1;

    for (let i = 0; i < markers.length; i++) {
      const marker = markers[i];
      if (
        !marker.characterStartTimesSeconds ||
        !marker.characterEndTimesSeconds
      )
        continue;

      const charIndex = marker.characterStartTimesSeconds.findIndex(
        (startTime, j) =>
          currentTime >= startTime &&
          currentTime <= marker.characterEndTimesSeconds![j],
      );

      if (charIndex !== -1) {
        currentMarkerIndex = i;
        currentCharIndex = charIndex;
        break;
      }
    }

    if (currentMarkerIndex === -1 || currentCharIndex === -1) return undefined;

    // Combine all marker characters into one text
    const fullText = markers.map((m) => m.characters?.join('') ?? '').join('');

    // Calculate the absolute character index in the combined text
    const absoluteCharIndex =
      markers
        .slice(0, currentMarkerIndex)
        .reduce((sum, m) => sum + (m.characters?.length ?? 0), 0) +
      currentCharIndex;

    // Find all matches in the full text
    const matches: { text: string; start: number; end: number }[] = [];
    let match;
    while ((match = boundaryRegex.exec(fullText)) !== null) {
      matches.push({
        text: match[0],
        start: match.index,
        end: match.index + match[0].length,
      });
      // Avoid infinite loop for zero-length matches
      if (match.index === boundaryRegex.lastIndex) {
        boundaryRegex.lastIndex++;
      }
    }

    // Find the match that contains our current character
    const currentMatch = matches.find(
      (m) => absoluteCharIndex >= m.start && absoluteCharIndex < m.end,
    );

    if (!currentMatch) return undefined;

    return {
      text: currentMatch.text,
      startIndex: currentMatch.start,
      endIndex: currentMatch.end - 1,
    };
  }
}
